import React, { useCallback, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  ButtonBase,
  Card,
  CardContent,
  Chip,
  Collapse,
  List,
  ListItemButton,
  Typography,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import BusinessIcon from '@mui/icons-material/Business';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { formatCurrency } from '../../../../shared/utils/currency';
import type { Deduction } from '../../types/potDetails.types';

interface DeductionGroup {
  groupName: string;
  deductions: Deduction[];
  totalAmount: number;
}

function groupDeductionsBySite(deductions: Deduction[]): DeductionGroup[] {
  const map = new Map<string, Deduction[]>();
  for (const d of deductions) {
    const key = d.siteName ?? 'Unassigned';
    const list = map.get(key) ?? [];
    list.push(d);
    map.set(key, list);
  }
  return Array.from(map.entries()).map(([groupName, items]) => ({
    groupName,
    deductions: items,
    // Cancelled deductions stay visible (struck through) but never count towards totals
    totalAmount: items.reduce(
      (sum, d) => (d.status === 'Cancelled' ? sum : sum + d.amount),
      0
    ),
  }));
}

interface PotDeductionsCardProps {
  deductions: Deduction[];
  onDeductionClick?: (deductionId: string) => void;
}

export function PotDeductionsCard({
  deductions,
  onDeductionClick,
}: PotDeductionsCardProps) {
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(
    new Set()
  );

  const toggleGroup = useCallback((key: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const groups = useMemo(() => groupDeductionsBySite(deductions), [deductions]);

  if (deductions.length === 0) return null;

  return (
    <Card
      variant="outlined"
      data-testid="pot-deductions-card"
      sx={{ height: '100%' }}
    >
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
        <Typography
          variant="h6"
          sx={{ px: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 3 }, pb: 1 }}
        >
          Deductions ({deductions.length})
        </Typography>

        <List disablePadding>
          {groups.map(group => {
            const expanded = !collapsedGroups.has(group.groupName);
            return (
              <Box key={group.groupName}>
                <ButtonBase
                  onClick={() => toggleGroup(group.groupName)}
                  aria-expanded={expanded}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    width: '100%',
                    px: { xs: 2, sm: 3 },
                    py: 1,
                    justifyContent: 'flex-start',
                    bgcolor: 'action.selected',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <ChevronRightIcon
                    fontSize="small"
                    sx={{
                      color: 'text.secondary',
                      transition: 'transform 150ms ease',
                      transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
                    }}
                  />
                  <BusinessIcon
                    sx={{
                      fontSize: 16,
                      color: 'text.secondary',
                      flexShrink: 0,
                    }}
                  />
                  <Typography variant="body2" fontWeight={600}>
                    {group.groupName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {group.deductions.length}
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color="error.main"
                    sx={{ ml: 'auto' }}
                  >
                    {formatCurrency(group.totalAmount)}
                  </Typography>
                </ButtonBase>

                <Collapse in={expanded} unmountOnExit>
                  {group.deductions.map(deduction => (
                    <DeductionListItem
                      key={deduction.id}
                      deduction={deduction}
                      onClick={onDeductionClick}
                    />
                  ))}
                </Collapse>
              </Box>
            );
          })}
        </List>
      </CardContent>
    </Card>
  );
}

const DeductionListItem = React.memo(function DeductionListItem({
  deduction,
  onClick,
}: {
  deduction: Deduction;
  onClick?: (deductionId: string) => void;
}) {
  const refLabel = deduction.uniqueReference
    ? `${deduction.uniqueReference} - ${deduction.description}`
    : deduction.description;

  const subheading =
    [deduction.siteName, deduction.plotReference].filter(Boolean).join(' · ') ||
    null;

  const isCancelled = deduction.status === 'Cancelled';

  return (
    <ListItemButton
      onClick={() => onClick?.(deduction.id)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        py: 1.5,
        px: { xs: 2, sm: 3 },
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Avatar
        sx={{
          width: 32,
          height: 32,
          flexShrink: 0,
          bgcolor: 'transparent',
        }}
      >
        <RemoveCircleOutlineIcon sx={{ color: 'error.main', fontSize: 20 }} />
      </Avatar>

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {refLabel}
        </Typography>
        {subheading && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
            }}
          >
            {subheading}
          </Typography>
        )}
      </Box>

      {isCancelled && (
        <Chip
          label="Cancelled"
          size="small"
          sx={{ fontWeight: 600, flexShrink: 0 }}
        />
      )}

      <Typography
        variant="body2"
        fontWeight={600}
        sx={{
          flexShrink: 0,
          whiteSpace: 'nowrap',
          color: isCancelled ? 'text.disabled' : 'error.main',
          textDecoration: isCancelled ? 'line-through' : 'none',
        }}
      >
        {formatCurrency(deduction.amount)}
      </Typography>
    </ListItemButton>
  );
});
